import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  FaFilter,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaReceipt,
  FaDownload,
  FaSync,
  FaCreditCard,
  FaFileInvoiceDollar,
  FaSearch,
  FaTimes,
  FaCalendarDay,
  FaDatabase,
  FaExclamationTriangle,
  FaCheckCircle,
  FaListAlt,
} from "react-icons/fa";
import "./FamilyPaymentHistory.css";

const FamilyPaymentHistory = ({ familyData }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDbConnected, setIsDbConnected] = useState(true);
  const [dbConnectionError, setDbConnectionError] = useState("");
  const [isCheckingDb, setIsCheckingDb] = useState(false);
  
  // Payment History State
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  
  // Filter states
  const [filters, setFilters] = useState({
    entryType: "all", // 'all', 'fee', 'expense'
    startDate: "",
    endDate: "",
    specificMonth: "",
  });
  
  // Available months for filter
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  
  // Error states
  const [paymentHistoryError, setPaymentHistoryError] = useState("");
  
  // Summary stats
  const [summary, setSummary] = useState({
    totalTransactions: 0,
    totalAmount: 0,
    totalPaid: 0,
    totalPending: 0,
    feeCount: 0,
    expenseCount: 0,
  });

  // Check database connection
  const checkDatabaseConnection = async () => {
    setIsCheckingDb(true);
    setDbConnectionError("");

    try {
      // Use the family API endpoint to test connection
      const response = await axios.post(
        "https://mhsdev.online/MHS-WEB/api/manage_family_accounts.php",
        {
          action: "get-family-payment-history",
          family_code: familyData?.familyCode || "TEST",
        },
        { timeout: 5000 }
      );

      if (response.data.success !== undefined || response.data.data !== undefined) {
        setIsDbConnected(true);
        return true;
      } else {
        setIsDbConnected(false);
        setDbConnectionError("Database connection test failed");
        return false;
      }
    } catch (error) {
      console.error("Database connection test failed:", error);
      setIsDbConnected(false);
      
      if (error.code === "ERR_NETWORK" || error.message.includes("Network Error")) {
        setDbConnectionError("Network error. Please check if XAMPP/WAMP is running.");
      } else if (error.response?.status === 404) {
        setDbConnectionError("API endpoint not found. Check file path.");
      } else {
        setDbConnectionError("Connection failed: " + error.message);
      }
      return false;
    } finally {
      setIsCheckingDb(false);
    }
  };

  // Fetch payment history for family
// Fetch payment history for family
const fetchFamilyPaymentHistory = async () => {
  if (!familyData?.familyCode) {
    setPaymentHistoryError("Family code is required");
    return;
  }

  setIsLoading(true);
  setPaymentHistoryError("");

  try {
    console.log("Fetching family payment history for:", familyData.familyCode);
    
    // Fetch fee records
    const feeResponse = await axios.post(
      "https://mhsdev.online/MHS-WEB/api/manage_family_accounts.php?action=search-family-fee-details",
      { familyCode: familyData.familyCode },
      { timeout: 10000 }
    );

    // Fetch expense records
    const expenseResponse = await axios.post(
      "https://mhsdev.online/MHS-WEB/api/manage_family_accounts.php?action=search-family-expense-details",
      { familyCode: familyData.familyCode },
      { timeout: 10000 }
    );

    console.log("Fee response:", feeResponse.data);
    console.log("Expense response:", expenseResponse.data);

    let allRecords = [];
    const seenTransactions = new Set(); // To track unique transactions

    // Process fee records
    if (feeResponse.data.success && feeResponse.data.feeDetails) {
      feeResponse.data.feeDetails.forEach(fee => {
        // Use transaction_id as unique key, fallback to id + timestamp
        const transactionKey = fee.transaction_id || 
          `${fee.id}_${fee.created_at || Date.now()}`;
        
        if (!seenTransactions.has(transactionKey)) {
          seenTransactions.add(transactionKey);
          
          allRecords.push({
            ...fee,
            record_type: "fee",
            type_display: "Monthly Fee",
            icon: "credit-card",
            color: "blue",
            // Ensure consistent field names
            amount: fee.monthly_fee || fee.amount || 0,
            paid_amount: fee.fee_paid || fee.paid_amount || 0,
            pending_amount: fee.fee_pending || fee.pending_amount || 0,
            payment_mode: fee.payment_mode || "cash",
            created_at: fee.created_at || fee.payment_date || new Date().toISOString(),
            description: `Monthly Fee - ${fee.paid_month || "N/A"}`,
            transaction_key: transactionKey
          });
        }
      });
    }

    // Process expense records
    if (expenseResponse.data.success && expenseResponse.data.expenseDetails) {
      expenseResponse.data.expenseDetails.forEach(expense => {
        const transactionKey = expense.transaction_id || 
          `${expense.id}_${expense.created_at || Date.now()}`;
        
        if (!seenTransactions.has(transactionKey)) {
          seenTransactions.add(transactionKey);
          
          allRecords.push({
            ...expense,
            record_type: "expense",
            type_display: "Expense Payment",
            icon: "receipt",
            color: "green",
            // Ensure consistent field names
            amount: expense.expense_payable || expense.amount || 0,
            paid_amount: expense.expense_paid || expense.paid_amount || 0,
            pending_amount: expense.expense_pending || expense.pending_amount || 0,
            payment_mode: expense.expense_type || expense.payment_mode || "other",
            created_at: expense.created_at || new Date().toISOString(),
            description: expense.description || expense.transaction_purpose || "Expense",
            transaction_key: transactionKey
          });
        }
      });
    }

    console.log("All unique records processed:", allRecords);

    // Sort by date (newest first)
    allRecords.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    setPaymentHistory(allRecords);
    setFilteredHistory(allRecords);
    
    // Calculate summary
    calculateSummary(allRecords);
    
    setIsDbConnected(true);
    
  } catch (error) {
    console.error("Error fetching family payment history:", error);
    
    let errorMessage = "Failed to fetch payment history";
    if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    setPaymentHistoryError(errorMessage);
    setPaymentHistory([]);
    setFilteredHistory([]);
    
    if (error.code === "ERR_NETWORK" || error.code === "ECONNABORTED") {
      setIsDbConnected(false);
      setDbConnectionError("Request timeout. Please try again.");
    }
  } finally {
    setIsLoading(false);
  }
};

  // Calculate summary statistics
  const calculateSummary = (records) => {
    const summary = {
      totalTransactions: records.length,
      totalAmount: 0,
      totalPaid: 0,
      totalPending: 0,
      feeCount: 0,
      expenseCount: 0,
    };

    records.forEach(record => {
      summary.totalAmount += parseFloat(record.amount || 0);
      summary.totalPaid += parseFloat(record.paid_amount || 0);
      summary.totalPending += parseFloat(record.pending_amount || 0);
      
      if (record.record_type === "fee") {
        summary.feeCount++;
      } else if (record.record_type === "expense") {
        summary.expenseCount++;
      }
    });

    setSummary(summary);
  };

  // Apply filters
  const applyFilters = () => {
    let filtered = [...paymentHistory];

    // Filter by entry type
    if (filters.entryType !== "all") {
      filtered = filtered.filter(record => record.record_type === filters.entryType);
    }

    // Filter by date range
    if (filters.startDate) {
      const startDate = new Date(filters.startDate);
      filtered = filtered.filter(record => {
        const recordDate = new Date(record.created_at);
        return recordDate >= startDate;
      });
    }

    if (filters.endDate) {
      const endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59, 999); // End of day
      filtered = filtered.filter(record => {
        const recordDate = new Date(record.created_at);
        return recordDate <= endDate;
      });
    }

    // Filter by specific month
    if (filters.specificMonth) {
      filtered = filtered.filter(record => {
        const recordDate = new Date(record.created_at);
        const monthName = months[recordDate.getMonth()];
        return monthName === filters.specificMonth;
      });
    }

    setFilteredHistory(filtered);
    calculateSummary(filtered);
  };

  // Handle filter changes
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  // Reset all filters
  const resetFilters = () => {
    setFilters({
      entryType: "all",
      startDate: "",
      endDate: "",
      specificMonth: "",
    });
    setFilteredHistory(paymentHistory);
    calculateSummary(paymentHistory);
  };

  // Format currency in Pakistani Rupees
  const formatCurrency = (amount) => {
    return `Rs. ${parseFloat(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Format date in Pakistani format
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-PK", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  // Get month name from date
  const getMonthName = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return months[date.getMonth()];
    } catch (error) {
      return "N/A";
    }
  };

  // Get status badge
  const getStatusBadge = (record) => {
    const pending = parseFloat(record.pending_amount || 0);
    const paid = parseFloat(record.paid_amount || 0);

    if (pending === 0) {
      return <span className="fph-status-badge fph-status-paid">Paid</span>;
    } else if (paid > 0) {
      return <span className="fph-status-badge fph-status-partial">Partial</span>;
    } else {
      return <span className="fph-status-badge fph-status-unpaid">Unpaid</span>;
    }
  };

  // Get type icon
  const getTypeIcon = (recordType) => {
    if (recordType === "fee") {
      return <FaCreditCard className="fph-type-icon fph-fee-icon" />;
    } else {
      return <FaReceipt className="fph-type-icon fph-expense-icon" />;
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    if (filteredHistory.length === 0) {
      alert("No data to export!");
      return;
    }

    let csvContent = "data:text/csv;charset=utf-8,";

    // Add headers
    csvContent += "Date,Type,Description,Amount,Paid,Pending,Payment Mode,Transaction ID,Status,Month\n";

    // Add rows
    filteredHistory.forEach((record) => {
      const pending = parseFloat(record.pending_amount || 0);
      const paid = parseFloat(record.paid_amount || 0);
      let status = "Unpaid";
      if (pending === 0) status = "Paid";
      else if (paid > 0) status = "Partial";

      const row = [
        formatDate(record.created_at),
        record.type_display || record.record_type,
        record.description || "",
        formatCurrency(record.amount),
        formatCurrency(record.paid_amount),
        formatCurrency(record.pending_amount),
        record.payment_mode || "N/A",
        record.transaction_id || "N/A",
        status,
        getMonthName(record.created_at),
      ].map(field => `"${field}"`).join(",");
      
      csvContent += row + "\n";
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `family_payment_history_${familyData?.familyCode || "family"}_${new Date().getTime()}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Initial fetch
  useEffect(() => {
    checkDatabaseConnection();
    if (familyData?.familyCode) {
      fetchFamilyPaymentHistory();
    }
  }, [familyData?.familyCode]);

  // Apply filters when they change
  useEffect(() => {
    if (paymentHistory.length > 0) {
      applyFilters();
    }
  }, [filters, paymentHistory]);

  return (
    <div className="family-payment-history-container">
      {/* Database Connection Status */}
      <div
        className={`fph-database-status ${
          isDbConnected ? "fph-connected" : "fph-disconnected"
        }`}
      >
        <div className="fph-database-status-content">
          {isDbConnected ? (
            <>
              <FaCheckCircle className="fph-database-status-icon" />
              <span className="fph-database-status-text">Database Connected</span>
            </>
          ) : (
            <>
              <FaExclamationTriangle className="fph-database-status-icon" />
              <span className="fph-database-status-text">
                Database Connection Issue:{" "}
                {dbConnectionError || "Unable to connect"}
              </span>
            </>
          )}
          <button
            className="fph-database-refresh-btn"
            onClick={checkDatabaseConnection}
            disabled={isCheckingDb}
          >
            {isCheckingDb ? <FaSync className="fph-spin" /> : "Retry"}
          </button>
        </div>
      </div>

      {/* Header */}
      <div className="fph-header">
        <h3>
          <FaListAlt className="fph-header-icon" />
          Family Payment History
          <span className="fph-subtitle">
            Family Code: {familyData?.familyCode || "N/A"}
            {familyData?.familyName && ` - ${familyData.familyName}`}
          </span>
        </h3>

        <div className="fph-actions">
          <button
            className="fph-export-btn"
            onClick={exportToCSV}
            disabled={filteredHistory.length === 0 || !isDbConnected}
          >
            <FaDownload className="fph-export-icon" />
            Export CSV
          </button>
          <button
            className="fph-refresh-btn"
            onClick={fetchFamilyPaymentHistory}
            disabled={isLoading || !isDbConnected}
          >
            <FaSync
              className={`fph-refresh-icon ${isLoading ? "fph-spin" : ""}`}
            />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {isDbConnected && filteredHistory.length > 0 && (
        <div className="fph-summary-cards">
          <div className="fph-summary-card">
            <div className="fph-summary-card-header">
              <FaFileInvoiceDollar className="fph-summary-card-icon total" />
              <span>Total Transactions</span>
            </div>
            <div className="fph-summary-card-content">
              <div className="fph-summary-card-value">
                {summary.totalTransactions}
              </div>
              <div className="fph-summary-card-subtitle">
                Total Amount: {formatCurrency(summary.totalAmount)}
              </div>
            </div>
          </div>

          <div className="fph-summary-card">
            <div className="fph-summary-card-header">
              <FaCreditCard className="fph-summary-card-icon fee" />
              <span>Fee Records</span>
            </div>
            <div className="fph-summary-card-content">
              <div className="fph-summary-card-value">
                {summary.feeCount}
              </div>
              <div className="fph-summary-card-subtitle">
                Monthly Fee Payments
              </div>
            </div>
          </div>

          <div className="fph-summary-card">
            <div className="fph-summary-card-header">
              <FaReceipt className="fph-summary-card-icon expense" />
              <span>Expense Records</span>
            </div>
            <div className="fph-summary-card-content">
              <div className="fph-summary-card-value">
                {summary.expenseCount}
              </div>
              <div className="fph-summary-card-subtitle">
                Expense Payments
              </div>
            </div>
          </div>

          <div className="fph-summary-card">
            <div className="fph-summary-card-header">
              <FaMoneyBillWave className="fph-summary-card-icon balance" />
              <span>Payment Summary</span>
            </div>
            <div className="fph-summary-card-content">
              <div className="fph-summary-card-value">
                {formatCurrency(summary.totalPaid)}
              </div>
              <div className="fph-summary-card-subtitle">
                Paid: {formatCurrency(summary.totalPaid)} | 
                Pending: {formatCurrency(summary.totalPending)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters Section */}
      <div className="fph-filters">
        <div className="fph-filter-group">
          <label className="fph-filter-label">
            <FaFilter className="fph-filter-icon" />
            Filter by Type
          </label>
          <div className="fph-filter-buttons">
            <button
              className={`fph-filter-btn ${
                filters.entryType === "all" ? "active" : ""
              }`}
              onClick={() => handleFilterChange("entryType", "all")}
              disabled={!isDbConnected}
            >
              All Transactions
            </button>
            <button
              className={`fph-filter-btn ${
                filters.entryType === "fee" ? "active" : ""
              }`}
              onClick={() => handleFilterChange("entryType", "fee")}
              disabled={!isDbConnected}
            >
              <FaCreditCard className="fph-filter-btn-icon" />
              Fee Only
            </button>
            <button
              className={`fph-filter-btn ${
                filters.entryType === "expense" ? "active" : ""
              }`}
              onClick={() => handleFilterChange("entryType", "expense")}
              disabled={!isDbConnected}
            >
              <FaReceipt className="fph-filter-btn-icon" />
              Expense Only
            </button>
          </div>
        </div>

        <div className="fph-filter-group">
          <label className="fph-filter-label">
            <FaCalendarAlt className="fph-filter-icon" />
            Date Range
          </label>
          <div className="fph-date-range">
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={(e) => handleFilterChange("startDate", e.target.value)}
              className="fph-date-input"
              placeholder="From Date"
              disabled={!isDbConnected}
            />
            <span className="fph-date-separator">to</span>
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={(e) => handleFilterChange("endDate", e.target.value)}
              className="fph-date-input"
              placeholder="To Date"
              disabled={!isDbConnected}
            />
          </div>
        </div>

        <div className="fph-filter-group">
          <label className="fph-filter-label">
            <FaCalendarDay className="fph-filter-icon" />
            Specific Month
          </label>
          <select
            value={filters.specificMonth}
            onChange={(e) => handleFilterChange("specificMonth", e.target.value)}
            className="fph-month-select"
            disabled={!isDbConnected}
          >
            <option value="">All Months</option>
            {months.map(month => (
              <option key={month} value={month}>{month}</option>
            ))}
          </select>
        </div>

        <div className="fph-filter-actions">
          <button
            className="fph-apply-btn"
            onClick={applyFilters}
            disabled={!isDbConnected}
          >
            <FaSearch className="fph-apply-icon" />
            Apply Filters
          </button>
          <button
            className="fph-reset-btn"
            onClick={resetFilters}
            disabled={!isDbConnected}
          >
            <FaTimes className="fph-reset-icon" />
            Reset All
          </button>
        </div>
      </div>

      {/* Database Warning */}
      {!isDbConnected && (
        <div className="fph-empty-state">
          <FaExclamationTriangle className="fph-empty-icon" />
          <h4>Database Connection Required</h4>
          <p>
            {dbConnectionError ||
              "Please check your database connection and try again."}
          </p>
          <button
            className="fph-empty-action"
            onClick={checkDatabaseConnection}
          >
            <FaSync className={isCheckingDb ? "fph-spin" : ""} />
            {isCheckingDb ? "Checking..." : "Retry Connection"}
          </button>
        </div>
      )}

      {/* Transaction Table */}
      <div className="fph-table-container" style={{overflow:'scroll'}}>
        {isLoading ? (
          <div className="fph-loading">
            <div className="fph-spinner"></div>
            <p>Loading payment history...</p>
          </div>
        ) : !isDbConnected ? (
          <div className="fph-empty-state">
            <FaDatabase className="fph-empty-icon" />
            <h4>Database Not Connected</h4>
            <p>
              Please check your database connection and server configuration.
            </p>
          </div>
        ) : paymentHistoryError ? (
          <div className="fph-empty-state">
            <FaExclamationTriangle className="fph-empty-icon" />
            <h4>Error Loading Payment History</h4>
            <p>{paymentHistoryError}</p>
            <button
              className="fph-empty-action"
              onClick={fetchFamilyPaymentHistory}
            >
              <FaSync className={isLoading ? "fph-spin" : ""} />
              Retry
            </button>
          </div>
        ) : filteredHistory.length === 0 ? (
          <div className="fph-empty-state">
            <FaFileInvoiceDollar className="fph-empty-icon" />
            <h4>No Transactions Found</h4>
            <p>
              {paymentHistory.length === 0 
                ? "No payment history found for this family." 
                : "No transactions match the selected filters."}
            </p>
            {paymentHistory.length > 0 && (
              <button className="fph-empty-action" onClick={resetFilters}>
                Reset Filters
              </button>
            )}
          </div>
        ) : (
          < >
            <table className="fph-table" style={{overflow:'scroll'}}>
              <thead>
                <tr>
                  <th>Date & Time</th>
                  <th>Type</th>
                  <th>Description</th>
                  <th>Month</th>
                  <th>Amount</th>
                  <th>Paid</th>
                  <th>Pending</th>
                  <th>Status</th>
                  <th>Payment Mode</th>
                  <th>Transaction ID</th>
                </tr>
              </thead>
              <tbody>
                {filteredHistory.map((record, index) => (
                  <tr key={index} className={`fph-row-${record.record_type}`}>
                    <td className="fph-date-cell">
                      {formatDate(record.created_at)}
                    </td>
                    <td className="fph-type-cell">
                      <div className="fph-type-wrapper">
                        {getTypeIcon(record.record_type)}
                        <span className={`fph-type-text ${record.record_type}`}>
                          {record.type_display || record.record_type}
                        </span>
                      </div>
                    </td>
                    <td className="fph-description-cell">
                      {record.description || "No description"}
                      {record.record_type === "fee" && record.paid_month && (
                        <div className="fph-remarks">
                          Month: {record.paid_month}
                          {record.class && ` | Class: ${record.class}`}
                        </div>
                      )}
                    </td>
                    <td className="fph-month-cell">
                      {getMonthName(record.created_at)}
                    </td>
                    <td className="fph-amount-cell">
                      <strong>{formatCurrency(record.amount)}</strong>
                    </td>
                    <td className="fph-paid-cell">
                      <span className="fph-paid-amount">
                        {formatCurrency(record.paid_amount)}
                      </span>
                    </td>
                    <td className="fph-pending-cell">
                      <span
                        className={`fph-pending-amount ${
                          parseFloat(record.pending_amount || 0) === 0
                            ? "zero"
                            : ""
                        }`}
                      >
                        {formatCurrency(record.pending_amount)}
                      </span>
                    </td>
                    <td className="fph-status-cell">
                      {getStatusBadge(record)}
                    </td>
                    <td className="fph-mode-cell">
                      <span
                        className={`fph-mode-badge ${record.payment_mode?.toLowerCase()}`}
                      >
                        {record.payment_mode || "N/A"}
                      </span>
                    </td>
                    <td className="fph-transaction-cell">
                      {record.transaction_id || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Results Summary */}
            <div className="fph-results-summary">
              <span className="fph-results-count">
                Showing {filteredHistory.length} of {paymentHistory.length} transactions
              </span>
              <span className="fph-results-total">
                Total: {formatCurrency(summary.totalAmount)} | 
                Paid: {formatCurrency(summary.totalPaid)} | 
                Pending: {formatCurrency(summary.totalPending)}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FamilyPaymentHistory;
